import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Zap } from 'lucide-react';
const RAND = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  maximumFractionDigits: 0
});
type Billing = 'monthly' | 'annual';
interface Tier {
  name: string;
  badge?: string;
  monthly: number | null; // null = custom
  annual: number | null; // per-month equivalent when billed annually
  descriptor: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}
const TIERS: Tier[] = [{
  name: 'Founding',
  badge: 'First 100 retailers',
  monthly: 2500,
  annual: 2000,
  descriptor: 'Lock in our lowest monthly rate for joining early.',
  features: ['Unlimited USSD, WhatsApp & SMS collections', 'Same-day PayShap settlement', 'Daily reconciliation & webhooks', 'Real-time dashboard & reporting', 'Dedicated integration support'],
  cta: 'Claim founding spot',
  highlighted: true
}, {
  name: 'Growth',
  monthly: 6500,
  annual: 5200,
  descriptor: 'For established retailers scaling collections across channels.',
  features: ['Everything in Founding', 'Priority support (4h response)', 'Advanced cohort & ageing reports', 'Multiple settlement accounts', 'Up to 10 team seats'],
  cta: 'Get started'
}, {
  name: 'Enterprise',
  monthly: null,
  annual: null,
  descriptor: 'For national chains with large books and strict compliance needs.',
  features: ['Everything in Growth', 'Volume-based custom pricing', 'SSO / SAML & audit exports', 'Dedicated success manager', '99.95% uptime SLA'],
  cta: 'Talk to sales'
}];
const INCLUDED_IN_ALL = ['Unlimited collections — no per-transaction fees', 'Same-day PayShap settlement', 'Bank-grade security & POPIA compliance'];
export function Pricing() {
  const [billing, setBilling] = useState<Billing>('monthly');
  return <section id="pricing" className="py-24 bg-ink" data-id="element-278">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-279">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-3xl mb-10" data-id="element-280">
          
          <div className="text-xs font-medium uppercase tracking-[0.2em] text-mint mb-4" data-id="element-281">
            Pricing
          </div>
          <h2 className="display text-bone mb-6" style={{
          fontSize: 'clamp(2.25rem, 5vw, 4rem)'
        }} data-id="element-282">
            
            Simple monthly{' '}
            <span className="display-italic text-mint" data-id="element-283">pricing</span>.
          </h2>
          <p className="text-lg text-bone-dim leading-relaxed" data-id="element-284">
            One flat monthly subscription with unlimited collections — no
            per-transaction fees, ever. Pick the plan that fits your book and
            switch any time.
          </p>
        </motion.div>

        {/* Billing toggle */}
        <div className="flex items-center justify-center mb-12" data-id="element-285">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-ink-2 border border-white/10" data-id="element-286">
            <button onClick={() => setBilling('monthly')} className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${billing === 'monthly' ? 'bg-mint text-ink' : 'text-bone-dim hover:text-bone'}`} data-id="element-287">
              
              Monthly
            </button>
            <button onClick={() => setBilling('annual')} className={`px-5 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2 ${billing === 'annual' ? 'bg-mint text-ink' : 'text-bone-dim hover:text-bone'}`} data-id="element-288">
              
              Annual
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${billing === 'annual' ? 'bg-ink/20 text-ink' : 'bg-mint-soft text-mint'}`} data-id="element-289">
                
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Tier grid */}
        <div className="grid lg:grid-cols-3 gap-6 items-stretch" data-id="element-290">
          {TIERS.map((tier, i) => {
          const isCustom = tier.monthly === null;
          const price = billing === 'annual' ? tier.annual : tier.monthly;
          return <motion.div key={tier.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className={`relative rounded-3xl p-8 flex flex-col ${tier.highlighted ? 'bg-ink-2 border-2 border-mint shadow-[0_0_60px_-20px_rgba(43,227,140,0.5)]' : 'bg-ink-2 border border-white/10'}`} data-id="element-291">
                
                {tier.badge && <div className="absolute -top-3 left-8 inline-flex items-center px-3 py-1 rounded-full bg-mint text-ink text-xs font-semibold uppercase tracking-[0.15em]" data-id="element-292">
                    {tier.badge}
                  </div>}

                <div className="text-sm font-semibold text-bone uppercase tracking-[0.15em] mb-4" data-id="element-293">
                  {tier.name}
                </div>

                <div className="flex items-end gap-1.5 mb-1" data-id="element-294">
                  <span className="display text-bone" style={{
                fontSize: 'clamp(2.5rem, 4vw, 3.25rem)',
                lineHeight: 1
              }} data-id="element-295">
                    
                    {isCustom ? 'Custom' : RAND.format(price as number)}
                  </span>
                  {!isCustom && <span className="text-muted mb-2 text-sm" data-id="element-296">/ mo</span>}
                </div>
                <div className="text-sm text-mint mb-4 h-5" data-id="element-297">
                  {isCustom ? 'Volume-based pricing' : billing === 'annual' ? 'billed annually · save 20%' : 'billed monthly'}
                </div>
                <p className="text-sm text-bone-dim mb-6" data-id="element-298">{tier.descriptor}</p>

                <ul className="space-y-3 mb-8 flex-1" data-id="element-299">
                  {tier.features.map(f => <li key={f} className="flex items-start gap-2.5" data-id="element-300">
                      <div className="w-5 h-5 rounded-full bg-mint-soft flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-301">
                        <Check className="w-3 h-3 text-mint" data-id="element-302" />
                      </div>
                      <span className="text-sm text-bone-dim" data-id="element-303">{f}</span>
                    </li>)}
                </ul>

                <a href="#book-demo" className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-semibold text-sm transition-colors ${tier.highlighted ? 'bg-mint text-ink hover:bg-mint-dim' : 'bg-ink-3 border border-white/10 text-bone hover:bg-ink-4'}`} data-id="element-304">
                  
                  {tier.cta}
                  <ArrowRight className="w-4 h-4" data-id="element-305" />
                </a>
              </motion.div>;
        })}
        </div>

        {/* Included in every plan */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-6 bg-ink-2 border border-white/5 rounded-3xl p-6 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-10 gap-y-3" data-id="element-306">
          
          <div className="flex items-center gap-2 text-sm font-medium text-bone" data-id="element-307">
            <Zap className="w-4 h-4 text-mint" data-id="element-308" />
            Included in every plan
          </div>
          {INCLUDED_IN_ALL.map(item => <div key={item} className="flex items-center gap-2 text-sm text-bone-dim" data-id="element-309">
            
              <Check className="w-4 h-4 text-mint flex-shrink-0" data-id="element-310" />
              {item}
            </div>)}
        </motion.div>
      </div>
    </section>;
}